import { FormEvent, useState } from 'react';
import { Eye, EyeOff } from '@/components/icons';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { useTheme } from '@/layout/useTheme';
import { apiError } from '@/lib/apiError';
import logoDark from '@/assets/logo-dark.svg';
import logoLight from '@/assets/logo-light.svg';
import { queueIntro } from '@/layout/IntroSplash';
import loginBg from '@/assets/login-bg.png';
import wizoneLogo from '@/assets/wizone-logo.png';

export function LoginPage() {
  const { user, login, loading } = useAuth();
  const loc = useLocation();
  const { theme } = useTheme();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  if (loading) return <FullScreen>Loading…</FullScreen>;
  if (user) {
    const target = (loc.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={target} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const signedIn = await login(identifier, password);
      queueIntro('welcome', signedIn?.fullName); // logo intro with a welcome note, once, after sign-in
    } catch (e) {
      // Never surface the raw server / DB / stack response. Map 401 to a login-
      // specific message; everything else goes through apiError (network → "can't
      // reach server", 5xx → "server ran into a problem", etc.).
      const status = (e as { response?: { status?: number } })?.response?.status;
      setErr(status === 401 ? "Those details don't match the pattern — check your username and password." : apiError(e, 'Login failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FullScreen>
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]/60 p-8 shadow-xl backdrop-blur-xl"
      >
        <div className="mb-6 flex flex-col gap-1">
          <div className="flex justify-center">
            <img
              src={theme === 'dark' ? logoLight : logoDark}
              alt="Grhamoy"
              className="h-36 w-auto object-contain"
            />
          </div>
        </div>

        {err && (
          <div className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-4 py-2 text-sm text-danger">
            {err}
          </div>
        )}

        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          Username or Email
        </label>
        <input
          type="text"
          autoFocus
          autoComplete="username"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className="mb-4 w-full rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-accent"
          placeholder="Username or email"
          required
        />

        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          Password
        </label>
        <div className="relative mb-1.5">
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] px-3 py-2.5 pr-10 text-sm text-[var(--text-primary)] outline-none focus:border-accent"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={() => setPwOpen(true)}
            className="shrink-0 text-[11px] font-semibold text-accent hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-gradient-to-br from-accent to-[#e07b00] py-2.5 text-sm font-bold text-white shadow transition hover:-translate-y-px hover:shadow-lg disabled:opacity-60"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="mt-4 text-center text-[11px] text-[var(--text-muted)]">
          Gauvin&apos;s Home Pvt Ltd · GRHAMOY ERP v0.1
        </p>
      </form>
      <ForgotPasswordModal open={pwOpen} onClose={() => setPwOpen(false)} defaultIdentifier={identifier} />
    </FullScreen>
  );
}

/** Login-screen backdrop (brand photo, veil, developer credit) — shared with the reset-password page. */
export function FullScreen({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--bg-primary)] p-6 text-[var(--text-primary)]">
      {/* Full-bleed brand photo — covers the whole viewport */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Subtle veil — radial vignette that darkens the corners so the login
          card in the middle stays legible without hiding the brand imagery. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: theme === 'dark'
            ? 'radial-gradient(ellipse at center, rgba(15,15,20,0.30) 0%, rgba(15,15,20,0.75) 100%)'
            : 'radial-gradient(ellipse at center, rgba(255,255,255,0.00) 0%, rgba(255,255,255,0.20) 100%)',
        }}
      />
      <div className="relative z-10 flex w-full items-center justify-center">
        {children}
      </div>
      {/* Developer credit — bottom-right corner */}
      <a
        href="https://wizone.ai/"
        target="_blank"
        rel="noopener noreferrer"
        title="Wizone AI Labs"
        className="absolute bottom-3 right-3 z-10 flex items-center gap-2.5 rounded-full border border-white/40 bg-white/15 py-1.5 pl-4 pr-3 shadow-[0_4px_14px_rgba(90,60,20,0.06),inset_0_1px_0_rgba(255,255,255,0.45)] backdrop-blur-sm transition hover:-translate-y-px hover:bg-white/25 sm:bottom-5 sm:right-5"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#1f1a14]">Built &amp; designed by</span>
        <span aria-hidden="true" className="h-5 w-px bg-black/10" />
        <img src={wizoneLogo} alt="Wizone AI Labs" className="h-9 w-auto" />
      </a>
    </div>
  );
}
